import Nav from 'components/Nav';
import Content from 'components/Content';
import Typography from 'components/Typography';
import Spacer from 'components/Spacer';
import Footer from 'components/Footer';
import MemberBio from 'components/MemberBio';

import classes from 'styles/pages/About/About.module.scss';

import Pfizer from 'public/brands/pfizer.svg';
import Ebay from 'public/brands/ebay.svg';
import Kpmg from 'public/brands/kpmg.svg';
import HomeDepot from 'public/brands/home-depot.svg';
import Pepsi from 'public/brands/pepsico.svg';

import { EMPLOYERS_LINK } from 'utils/constants/urls';
import LAWYER_HOURLY_RATE from 'utils/constants/attorneys';

const companies = [Pfizer, Ebay, Kpmg, HomeDepot, Pepsi];

const navLinks = [
  { text: 'Why MetLife Legal Plans?', href: '/why-enroll' },
  { text: 'How We Work', href: '/#how-we-work' },
  { text: 'Reviews', href: '/#reviews' },
  { text: 'About Us', href: '/about-us' },
  { text: 'Diversity', href: '/diversity' },
  { text: 'For Employers', href: EMPLOYERS_LINK, external: true },
];

const team = [
  {
    blue: true,
    left: true,
    imageName: '01-Ingrid-Tolentino.png',
    name: 'Ingrid Tolentino',
    title: 'CEO',
    description: [
      'Ingrid Tolentino leads with single-minded vision and audacity. A seasoned executive and MetLife veteran, she has established MetLife Legal Plans as a versatile and relevant product for a changing and diverse workforce.',
      "Ingrid's expertise is in business and operational transformation, change management, and talent development. She has held multiple executive roles within MetLife, including Vice President, Business Services & Initiatives for the Property & Casualty business, Planning Officer for MetLife, and Chief of Staff to the President of Group Property & Casualty. As a leader, Ingrid continuously pushes the boundaries of our capabilities and offering while remaining committed to employee success and customer satisfaction.",
    ],
  },
  {
    blue: false,
    left: false,
    imageName: '02-Kris-Borer.png',
    name: 'Kris Borer',
    title: 'CHIEF TECHNOLOGY OFFICER',
    description: [
      'As Chief Technology Officer, Kris ensures that tech integration, software development, infrastructure, and digital product innovation at MetLife Legal Plans is part of the company’s regular operations.',
      'Before joining MetLife Legal Plans, Kris served as CTO for startups in legal technology, automation, robotics, and artificial intelligence. Kris has degrees in engineering from Carnegie Mellon University and the University of Pennsylvania.',
    ],
  },
  {
    blue: false,
    left: true,
    imageName: '03-Roger-Elder.png',
    name: 'Roger Elder',
    title: 'GENERAL COUNSEL',
    description: [
      'Roger manages the legal function for MetLife Legal Plans and Metropolitan General Insurance Company. In addition to ensuring that our business remains compliant, he provides legal support for our aggressive growth strategies.',
      'Before joining MetLife Legal Plans, Roger was Senior Counsel at MetLife, focusing on insurance regulation, customer and vendor agreements, insolvency, and privacy law. He began his legal career as an associate at Pillsbury Winthrop Shaw Pittman LLP. A writer, musician, and language lover, Roger received his bachelor’s degree in elementary and early childhood education from Temple University and his Juris Doctor degree from the University of Houston.',
    ],
  },
  {
    blue: false,
    left: false,
    imageName: '04-Gary-Glacken.png',
    name: 'Gary Glacken',
    title: 'CHIEF FINANCIAL OFFICER',
    description: [
      'A dedicated finance professional, Gary is responsible for the overall financial management of MetLife Legal Plans, including accounting, financial planning and analysis, pricing, and development of strategies for profitable growth.',
      'Gary most recently served as a vice president and CFO for MetLife’s Small Market and Specialty business segments. In addition to his 15 years of service within MetLife, he also held roles at Horizon Blue Cross Blue Shield, Ernst & Young, and Prudential. Gary holds Bachelor of Science and Master of Business Administration degrees in finance from Rutgers University.',
    ],
  },
  {
    blue: false,
    left: true,
    imageName: '05-Claire-Godlewski.png',
    name: 'Claire Godlewski',
    title: 'VP, CHIEF ADMINISTRATION OFFICER',
    description: [
      'Claire heads our member-facing Administration team. As the pulse of our organization, her team is responsible for all member-related engagement. Claire is dedicated to enhancing customer satisfaction and building strong customer relationships.',
      'Claire joined MetLife Legal Plans in 2002. Before assuming her current position in 2015, she managed Regional Market, MetLife and the Mercer Platform, National Accounts, and served as a Supervisor in Large for Group Administration. Claire is a graduate of Ohio University with a Bachelor of Business Administration.',
    ],
  },
  {
    blue: true,
    left: false,
    imageName: '06-Andrea-Quadros.png',
    name: 'Andrea Quadros',
    title: 'ENTERPRISE STRATEGY AND TRANSFORMATION LEADER',
    description: [
      'Andrea is an experienced strategist with a passion for talent and focuses on delivering MetLife Legal Plans’ growth and transformation plan. She is responsible for driving solutions and building organizational culture and capabilities to deliver a successful strategy. Andrea previously led Strategy and Planning for MetLife’s U.S. Business and held sales, product, and marketing positions across multiple MetLife regions. She started her career at McKinsey & Company in India, followed by strategy roles across developed and emerging markets. Andrea holds an MBA from Harvard Business School and a Bachelor’s degree from St. Xavier’s College, India.',
    ],
  },
  {
    blue: true,
    left: true,
    imageName: '07-Loi-Stoddard-Graham.png',
    name: 'Loi Stoddard-Graham',
    title: 'VP, PRODUCT & BUSINESS DEVELOPMENT',
    description: [
      'Loi is responsible for product strategy, marketing, competitive positioning, sales support, and implementation. A savvy business strategist, she enhances legal plan products with new features, builds innovative distribution relationships, and brokers our expansion into new markets—to create the best legal product for working families.',
      'A 20-year MetLife veteran, her experience spans the gamut, including product management of PlanSmart Financial Wellness, business development for MetLife Global Employee Benefits, Chief of Staff to the head of Employee Benefit Sales in U.S. Group Benefits, and roles within the Financial Management Group, Retail Sales, Group Sales, and Underwriting. She holds a Bachelor of Science from Fordham University, Masters Certificate in Business Intelligence from Villanova University, and attended a management program at the Tuck School of Business.',
    ],
  },
];

const About = () => (
  <>
    <Nav links={navLinks} />
    <main role="main">
      <div className={classes.header}>
        <div className={classes.tagline}>
          <div className={classes.aboutHeading}>
            <Typography bold className={classes.about}>
              About Us
            </Typography>
            <Spacer v={48} />
            <Typography className={classes.protection}>Protection at every step.</Typography>
          </div>
        </div>
      </div>

      <Spacer v={50} />

      <Content>
        <div className={classes.whoWeAre}>
          <Typography className={classes.section} unsetColor>
            Who <strong>We Are</strong>
          </Typography>

          <Spacer v={50} />

          <Typography>
            Since 1977, MetLife Legal Plans, formerly Hyatt Legal Plans, has been helping employees
            and families navigate legal issues by providing legal consultation and representation
            through a simple to use and cost-effective legal plan. Today, we are the country's
            largest provider of legal benefits. With a vast attorney network, an award-winning
            customer service team, and new digital solutions for legal needs&#8212;we're proud to do
            our part in expanding legal access to Americans.
          </Typography>

          <Spacer v={16} />

          <Typography>
            Our work is critical to upholding justice, and a culture of Diversity, Equity, and
            Inclusion is integral to how we do business and serve our customers. We're proud to
            offer legal coverage which improves outcomes for a diverse, multi-generational
            workforce. Legal plans improve employees' quality of life by helping with stress,
            feelings of safety, and financial concerns while allowing employers to provide
            competitive benefit packages that appeal to future talent.
          </Typography>
        </div>

        <Spacer v={50} />

        <div className={classes.contentWithPadding}>
          <Typography className={classes.section} style={{ color: '#0091D8' }}>
            What <strong> We Do </strong>
          </Typography>
        </div>

        <div className={classes.gradientBar} />

        <div className={classes.contentWithPadding}>
          <Typography>
            We empower employees by offering protection at every step. For a competitive monthly
            cost, MetLife Legal Plans provides financial security that comes with knowing an
            attorney is helping with your legal issues. Employees have unlimited access to a network
            of over 18,000 qualified and experienced attorneys to help them navigate simple and
            complex legal matters.{' '}
            <strong>Plans cover employees, their spouses, and dependents.</strong>
          </Typography>

          <Spacer v={16} />

          <Typography>Protection at every step is made possible because of:</Typography>

          <Spacer v={16} />

          <div className={classes.contentWithPadding}>
            <div className={classes.whatWeDoItem}>
              <img src="about/icon-cost-effectiveness.png" alt="" className={classes.icon} />
              <div>
                <Typography style={{ color: '#0091D8' }}>
                  <strong> COST-EFFECTIVENESS </strong>
                </Typography>

                <Spacer v={16} />

                <Typography>
                  The average attorney fee runs ${LAWYER_HOURLY_RATE} an hour.<sup>1</sup>{' '}
                  <strong>
                    Compare that to the cost of MetLife Legal Plans for the whole year ($240).
                  </strong>
                </Typography>
              </div>
            </div>

            <Spacer v={50} />

            <div className={classes.whatWeDoItem}>
              <img src="about/icon-accessibility.png" alt="" className={classes.icon} />
              <div>
                <Typography style={{ color: '#0091D8' }}>
                  <strong>ACCESSIBILITY</strong>
                </Typography>

                <Spacer v={16} />

                <Typography>
                  The legal plan benefits all employees regardless of gender, race,
                  sexual-orientation, and financial circumstances. Additionally, the plan{' '}
                  <strong>
                    includes over 1,700 self-help documents, and the searchable attorney network
                    makes it easy to find an attorney in your area that meets your needs.
                  </strong>
                </Typography>
              </div>
            </div>

            <Spacer v={50} />

            <div className={classes.whatWeDoItem}>
              <img
                src="about/icon-range-of-covered-legal-services.png"
                alt=""
                className={classes.icon}
              />
              <div>
                <Typography style={{ color: '#0091D8' }}>
                  <strong> RANGE OF COVERED LEGAL SERVICES </strong>
                </Typography>

                <Spacer v={16} />

                <Typography>
                  We have one of the most robust legal coverage options in the market, and are
                  rigorously expanding our service offerings, attorney network, and improving the
                  experience.
                </Typography>
              </div>
            </div>
          </div>
        </div>

        <Spacer v={50} />

        <div className={classes.contentWithPadding}>
          <Typography className={classes.section} style={{ color: '#0091D8' }}>
            Powerful and <strong>backed by MetLife </strong>
          </Typography>
        </div>

        <div className={classes.gradientBar} />

        <div className={classes.contentWithPadding}>
          <div className={classes.metlifeBuilding}>
            <img src="/metlife-building-2.png" alt="" />
          </div>

          <div className={classes.pictureText}>
            <Typography bold>
              For 25 years, we've been part of MetLife's suite of products. Legal plans are a
              versatile employee benefit.
            </Typography>

            <Spacer v={16} />

            <Typography>
              Legal plans work standalone but also supplements other MetLife benefits such as Pet
              Insurance, Vision, Dental, and Critical Illness. From start to finish, MetLife is
              committed to your success. Consolidating multiple product offerings with a single
              carrier streamlines the end-to-end service experience and creates valuable
              administrative efficiencies for customers.
            </Typography>
          </div>
        </div>

        <Spacer v={50} />

        <div className={classes.contentWithPadding}>
          <Typography className={classes.section} style={{ color: '#A5CC56' }}>
            How <strong> We Stand Apart </strong>
          </Typography>
        </div>

        <div className={classes.gradientBar} />

        <div className={classes.contentWithPadding}>
          <Typography>
            We ensure the highest level of care when helping resolve your legal issues. Our Client
            Service Center is consistently recognized for best-in-class customer service by the
            <a
              href="https://stevieawards.com/aba/metlife-legal-plans-best-legal-website"
              target="_blank"
              rel="noreferrer"
            >
              {' '}
              American Business Awards{' '}
            </a>
            and our attorney network includes thoroughly vetted professionals with an average of 25
            years of legal experience.
          </Typography>
        </div>

        <Spacer v={50} />

        <div className={classes.awards}>
          <img src="about/medal-4.png" className={classes.medal} alt="" />
          <img src="about/medal-3.png" className={classes.medal} alt="" />
          <img src="about/medal-1.png" className={classes.medal} alt="" />
          <img src="about/medal-2.png" className={classes.medal} alt="" />
        </div>

        <Spacer v={50} />

        <div className={classes.contentWithPadding}>
          <Typography>
            <strong>
              Our plans cover money matters, home and real estate, family and personal issues, civil
              lawsuits, estate planning, immigration help, document prep, elder-care issues, vehicle
              and driving incidents, and more.
            </strong>
            <br />
            Additionally, we are on the lookout for enhancements that are inclusive of the legal
            needs of all our members.
          </Typography>

          <Spacer v={50} />

          <Typography>
            <strong> Recently, we've expanded our offerings to include: </strong>
          </Typography>

          <Spacer v={24} />

          <div>
            <li>Coverage of up to eight parents or grandparents through Plus Parents </li>
            <li>
              Credit monitoring and premium identity protection through IdentityForce
              <sup>&reg;</sup>
            </li>
            <li>
              Caregiving benefits through Family First<sup>&reg;</sup>
            </li>
            <li> Divorce coverage</li>
            <li> Reproductive Assistance Law coverage </li>
            <li>
              Digital Estate Planning solution—complete wills, trusts, health proxy, and
              power-of-attorneys quickly and easily online
            </li>
          </div>

          <Spacer v={24} />

          <Typography>
            Except for plan exclusions, how you use the plan is limited only by your imagination.
          </Typography>
        </div>

        <Spacer v={50} />
      </Content>

      <div className={classes.sponsorsWrapper}>
        <div className={classes.sponsors}>
          {companies.map((Logo, idx) => (
            <div key={idx} className={classes.logo}>
              <Logo />
            </div>
          ))}
        </div>
        <div className={classes.banner}>
          <div className={classes.bannerText}>
            <Typography>
              <strong>
                OVER 220 OF
                <br />
                FORTUNE 500<sup>&copy;</sup>
                <br />
                COMPANIES{' '}
              </strong>
              TRUST
              <br />
              US WITH THEIR
              <br />
              EMPLOYEES
            </Typography>
          </div>
        </div>
      </div>

      <Content>
        <Spacer v={48} />
        <div className={classes.meetTeamHeader}>
          <Typography className={classes.meetTeam}>
            Meet <b>Our Team</b>
          </Typography>
          <Spacer h={24} />
        </div>

        <Spacer v={48} />

        {team.map(({ blue, left, imageName, name, title, description }) => {
          return (
            <MemberBio
              key={name}
              blue={blue}
              left={left}
              imageName={imageName}
              name={name}
              title={title}
              bio={description}
            />
          );
        })}

        <Typography className={classes.infoText}>
          <sup>1</sup>Average hourly rate of of ${LAWYER_HOURLY_RATE}.00 based on years of legal
          experience, National Law Journal and ALM Legal Intelligence, Survey of Law Firm Economics,
          (2021).
          <br />
          <br />
          Group legal plans provided by MetLife Legal Plans, Inc., Cleveland, Ohio. In certain
          states, group legal plans are provided through insurance coverage underwritten by
          Metropolitan General Insurance Company, Warwick, RI. Some services not available in all
          states. No service, including consultations, will be provided for: 1) employment-related
          matters, including company or statutory benefits; 2) matters involving the employer,
          MetLife and affiliates and plan attorneys; 3) matters in which there is a conflict of
          interest between the employee and spouse or dependents in which case services are excluded
          for the spouse and dependents; 4) appeals and class actions; 5) farm and business matters,
          including rental issues when the participant is the landlord; 6) patent, trademark and
          copyright matters; 7) costs and fines; 8) frivolous or unethical matters; 9) matters for
          which an attorney client relationship exists prior to the participant becoming eligible
          for plan benefits. For all other personal legal matters, an advice and consultation
          benefit is provided. Additional representation is also included for certain matters.
          Please see your plan description for details. MetLife® is a registered trademarks of
          MetLife Services and Solutions, LLC, New York, NY.
        </Typography>
      </Content>
    </main>

    <Footer />
  </>
);

export default About;
